import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return 'N/D';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

// ─── TEMA PO INDEKSU CARRA ────────────────────────────────────────────────────
const getCarroTheme = (carroIndex) => {
  if (carroIndex === 0) return {
    color:  'text-blue-500',
    bg:     'bg-blue-500/10',
    border: 'border-blue-500/25',
    dot:    'bg-blue-500',
    label:  'text-blue-500',
  };
  return {
    color:  'text-amber-700',
    bg:     'bg-amber-800/10',
    border: 'border-amber-700/25',
    dot:    'bg-amber-700',
    label:  'text-amber-700',
  };
};

// ─── RIGA SINGOLO CARRO ───────────────────────────────────────────────────────
const CarroRow = ({ carro, carroIndex, ncilindro, dataintervento }) => {
  const theme = getCarroTheme(carroIndex);
  const days  = daysSince(dataintervento);
  const has   = !!ncilindro;

  return (
    <div className={`flex flex-col gap-2 px-4 py-3 rounded-2xl border transition-all ${
      has
        ? `${theme.bg} ${theme.border}`
        : 'bg-muted/5 border-dashed border-border/20 opacity-40'
    }`}>
      {/* Riga 1: icona + nome carro */}
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${has ? theme.bg : 'bg-muted/20'}`}>
          <Icon name="Settings2" size={11} className={has ? theme.color : 'text-muted-foreground/20'} />
        </div>
        <span className={`text-[9px] font-black uppercase tracking-widest leading-none ${has ? theme.label : 'text-muted-foreground/25'}`}>
          {carro}
        </span>
      </div>

      {/* Riga 2: cilindro grande + data/giorni a destra */}
      <div className="flex items-end justify-between gap-2 pl-8">
        <span className={`text-base font-black tracking-tight leading-none ${has ? 'text-foreground' : 'text-muted-foreground/20'}`}>
          {has ? `${ncilindro}` : '—'}
        </span>

        {has && (
          <div className="flex flex-col items-end gap-0.5 shrink-0">
            <div className={`flex items-center gap-1 text-[9px] font-black ${theme.color}`}>
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${theme.dot}`} />
              {days === 0 ? 'Oggi' : `${days}gg`}
            </div>
            <span className="text-[9px] font-bold text-muted-foreground/50 tabular-nums leading-none">
              {formatDate(dataintervento)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── CARD SINGOLA LINEA ───────────────────────────────────────────────────────
const LineaCard = ({ linea, cells, colata, carriKeys }) => {
  const hasAny = carriKeys.some(c => cells[`${linea}||${c}`]);

  return (
    <div className={`relative flex flex-col p-5 rounded-[2rem] border transition-all duration-200 hover:shadow-lg hover:shadow-black/5 ${
      hasAny
        ? 'bg-card border-border/40 hover:border-primary/20'
        : 'bg-muted/5 border-dashed border-border/20 opacity-50'
    }`}>
      {/* Header */}
      <div className="mb-4">
        <span className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/35 block mb-1">
          {colata}
        </span>
        <span className="text-4xl font-black uppercase tracking-tighter text-foreground leading-none">
          {linea}
        </span>
      </div>

      {/* Righe carri */}
      <div className="flex flex-col gap-2">
        {carriKeys.map((carro, idx) => {
          const d = cells[`${linea}||${carro}`];
          return (
            <CarroRow
              key={carro}
              carro={carro}
              carroIndex={idx}
              ncilindro={d?.ncilindro ?? null}
              dataintervento={d?.dataintervento ?? null}
            />
          );
        })}
      </div>
    </div>
  );
};

// ─── SKELETON ─────────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="flex flex-col gap-4 p-5 rounded-[2rem] bg-muted/10 border border-border/20 animate-pulse">
    <div className="h-2 w-12 rounded bg-muted/40" />
    <div className="h-10 w-28 rounded-lg bg-muted/40" />
    <div className="flex flex-col gap-2">
      <div className="h-16 rounded-2xl bg-muted/30" />
      <div className="h-16 rounded-2xl bg-muted/30" />
    </div>
  </div>
);

// ─── COMPONENTE PRINCIPALE ────────────────────────────────────────────────────
const LineeOverviewModal = ({ isOpen, onClose }) => {
  const [selectedColata, setSelectedColata] = useState('CCA');
  const [cells, setCells]                   = useState({});
  const [isLoading, setIsLoading]           = useState(false);
  const [lineeKeys, setLineeKeys]           = useState(['1', '2', '3', '4']);
  const [carriKeys, setCarriKeys]           = useState(['CARRO PANIERA 1', 'CARRO PANIERA 2']);

  const fetchData = async (colata) => {
    setIsLoading(true);
    try {
      const { data: rows, error } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, linea, carropaniera, dataintervento, tipointervento, colata')
        .ilike('colata', colata)
        .ilike('tipointervento', 'MONTATO')
        .not('ncilindro', 'is', null)
        .not('linea', 'is', null)
        .not('carropaniera', 'is', null)
        .order('dataintervento', { ascending: false });

      if (error) throw error;

      const lineeSet = new Set();
      const carriSet = new Set();
      for (const r of rows ?? []) {
        if (r.linea)        lineeSet.add(r.linea);
        if (r.carropaniera) carriSet.add(r.carropaniera);
      }

      const lineeOrdered = [...lineeSet].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      const carriOrdered = [...carriSet].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

      if (lineeOrdered.length > 0) setLineeKeys(lineeOrdered);
      if (carriOrdered.length > 0) setCarriKeys(carriOrdered);

      const newCells = {};
      for (const r of rows ?? []) {
        const key = `${r.linea}||${r.carropaniera}`;
        if (!newCells[key]) {
          newCells[key] = { ncilindro: r.ncilindro, dataintervento: r.dataintervento };
        }
      }

      setCells(newCells);
    } catch (err) {
      console.error('LineeOverview fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
    const now = new Date().toLocaleString('it-IT');

    const fetchColataData = async (colata) => {
      const { data } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, linea, carropaniera, dataintervento, colata')
        .ilike('colata', colata)
        .ilike('tipointervento', 'MONTATO')
        .not('ncilindro', 'is', null)
        .order('linea', { ascending: true });
      return data || [];
    };

    const renderColataPage = (colataName, data, isNewPage = false) => {
      if (isNewPage) doc.addPage();

      // Header report
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.setFont('helvetica', 'normal');
      doc.text('OrizonDesk - Monitoraggio Linee', 14, 10);
      
      doc.setFontSize(22);
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.text(`MONITORAGGIO LINEE - ${colataName}`, 14, 22);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text(`Stato attuale al: ${now}`, 14, 28);
      
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.5);
      doc.line(14, 32, 196, 32);

      // Priprema podataka za tabelu
      const tableRows = [];
      const linee = [...new Set(data.map(d => d.linea))].sort();
      const carri = [...new Set(data.map(d => d.carropaniera))].sort();

      linee.forEach(l => {
        carri.forEach(c => {
          const found = data.find(item => item.linea === l && item.carropaniera === c);
          tableRows.push([
            `LINEA ${l}`,
            c,
            found ? found.ncilindro : '---',
            found ? new Date(found.dataintervento).toLocaleDateString('it-IT') : '---',
            found ? `${daysSince(found.dataintervento)} gg` : '---'
          ]);
        });
      });

      autoTable(doc, {
        startY: 40,
        head: [['LINEA', 'CARRO PANIERA', 'N° CILINDRO', 'DATA MONT.', 'GIORNI']],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: colataName === 'CCA' ? [37, 99, 235] : [15, 23, 42], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: { 0: { fontStyle: 'bold' }, 2: { fontStyle: 'bold' } }
      });

      // Footer
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setDrawColor(230);
      doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.setFont('helvetica', 'normal');
      doc.text(`Stampato il: ${now}`, 14, pageHeight - 10);
      doc.setFont('helvetica', 'bold');
      doc.text('OrizonDesk', pageWidth / 2, pageHeight - 10, { align: 'center' });
      doc.setTextColor(37, 99, 235);
      doc.text('https://desk.orizon-aix.com', pageWidth - 14, pageHeight - 10, { align: 'right' });
    };

    const dataCCA = await fetchColataData('CCA');
    const dataCCB = await fetchColataData('CCB');

    renderColataPage('CCA', dataCCA, false);
    renderColataPage('CCB', dataCCB, true);

    doc.save(`Monitoraggio_Linee_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  useEffect(() => {
    if (isOpen) fetchData(selectedColata);
  }, [selectedColata, isOpen]);

  const totalMontati = lineeKeys.reduce((sum, linea) =>
    sum + carriKeys.filter(c => cells[`${linea}||${c}`]).length, 0
  );
  const totalSlots = lineeKeys.length * carriKeys.length;

  if (!isOpen) return null;
return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-2 sm:p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="relative w-full max-w-5xl bg-card border border-border shadow-2xl rounded-[2rem] sm:rounded-[3rem] overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh] animate-in zoom-in-95 duration-300">

        {/* ── HEADER ── */}
        <div className="shrink-0 px-5 sm:px-8 py-4 sm:py-6 border-b border-border/40 bg-card/80 backdrop-blur-sm">

          {/* Glavna linija headera - flex-wrap dodan za mobilne */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            
            {/* Naslovna sekcija */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl sm:rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-sm shrink-0">
                <Icon name="Activity" size={18} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-black uppercase tracking-tighter text-foreground truncate">
                  Monitoraggio Linee
                </h2>
                <p className="text-[8px] sm:text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/40 mt-0.5 truncate">
                  Cilindri MONTATI
                </p>
              </div>
            </div>

            {/* Kontrole (Dugmići) */}
            <div className="flex items-center gap-2 ml-auto sm:ml-0">
              
              {/* CCA / CCB Toggle - Responzivni padding */}
              <div className="flex gap-0 p-1 rounded-xl sm:rounded-2xl bg-muted/60 border border-border/50">
                {['CCA', 'CCB'].map(c => {
                  const isActive = selectedColata === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setSelectedColata(c)}
                      className={`relative px-3 sm:px-6 py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all duration-200 ${
                        isActive
                          ? 'bg-foreground text-background shadow-md'
                          : 'text-muted-foreground/60 hover:text-foreground'
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>

              {/* PDF Button */}
              <button
                onClick={generatePDF}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-500 hover:bg-blue-500 hover:text-white transition-all shrink-0"
                title="Genera PDF Report"
              >
                <Icon name="FileDown" size={14} />
              </button>
              
              {/* Refresh Button */}
              <button
                onClick={() => fetchData(selectedColata)}
                disabled={isLoading}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-muted/40 border border-border/30 text-muted-foreground hover:text-primary hover:border-primary/30 transition-all disabled:opacity-40 shrink-0"
              >
                <Icon name="RefreshCw" size={14} className={isLoading ? 'animate-spin' : ''} />
              </button>

              {/* Close Button - Jasno odvojen */}
              <button
                onClick={onClose}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg sm:rounded-2xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/10 text-red-500/70 hover:text-red-500 transition-all group shrink-0"
              >
                <Icon name="X" size={16} className="group-hover:rotate-90 transition-transform duration-200" />
              </button>
            </div>
          </div>

          {/* Donja linija headera (Badge i Legenda) */}
          <div className="mt-4 sm:mt-5 flex flex-wrap items-center gap-3">
            <div className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl sm:rounded-2xl bg-foreground text-background flex items-center gap-2">
              <Icon name="Layers" size={12} />
              <span className="text-xs sm:text-base font-black uppercase tracking-widest">{selectedColata}</span>
            </div>

            <div className="px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-2xl bg-muted/40 border border-border/30 flex items-center gap-1.5">
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-muted-foreground/70">
                {totalMontati}
              </span>
              <span className="text-[9px] font-black text-muted-foreground/30">/</span>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-muted-foreground/40">
                {totalSlots}
              </span>
            </div>

            {/* Legenda - Skriva se na baš malim ekranima ili se spušta dole */}
            <div className="flex-1 flex justify-end gap-3 sm:gap-5">
              {carriKeys.map((c, idx) => {
                const theme = getCarroTheme(idx);
                return (
                  <div key={c} className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${theme.dot}`} />
                    <span className={`text-[8px] sm:text-[9px] font-black uppercase tracking-widest ${theme.label}`}>
                      {c.replace('CARRO PANIERA', 'CARRO ')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── GRID LINEE ── */}
        <div
          className="flex-1 overflow-y-auto p-4 sm:p-8"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
        >
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : lineeKeys.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Icon name="Inbox" size={36} className="text-muted-foreground/20" />
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/30">
                Nessun record
              </span>
            </div>
          ) : (
            <div className={`grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 ${
              lineeKeys.length >= 4 ? 'lg:grid-cols-4' : `lg:grid-cols-${lineeKeys.length}`
            }`}>
              {lineeKeys.map(linea => (
                <LineaCard
                  key={linea}
                  linea={linea}
                  cells={cells}
                  colata={selectedColata}
                  carriKeys={carriKeys}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LineeOverviewModal;